<template>
  <div>
    <v-card
      v-if="!editStore.initialized"
      :class="$vuetify.breakpoint.xs ? 'mt-2' : 'mt-4'"
      flat
      class="d-flex flex-column rounded-lg text-center pa-8"
      :color="$vuetify.theme.dark ? 'rgba(1, 105, 238, 0.1)' : 'rgba(1, 105, 238, 0.05)'"
    >
      <div class="my-auto">
        <v-progress-circular
          v-if="cardLoading"
          indeterminate
          size="40"
          class="mb-2"
          color="primary"
        ></v-progress-circular>
        <v-card-text class="text-h6 font-weight-bold pa-0 primary--text">
          <div>
            {{ loadingHeading }}
          </div>
          <div v-if="loadingDescription" class="text-subtitle-1">
            {{ loadingDescription }}
          </div>
        </v-card-text>
      </div>
    </v-card>
    <v-row v-if="!loading" :class="editStore.initialized ? 'editor-row' : 'editor-row opacity-0'">
      <v-col class="d-none d-sm-block" cols="12" lg="4">
        <div class="d-flex justify-space-between align-center mb-2">
          <div class="d-flex align-center">
            <v-btn
              icon
              @click="
                subtitlesEditMounted = true;
                editStore.editSubtitles = true;
              "
              :loading="editStore.fetchingTranscript || editStore.fetchingEmojis"
              class="mr-2"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <h3>{{ $t("subtitles") }}</h3>
          </div>
          <v-switch v-model="editStore.enableSubtitles" inset dense hide-details class="ma-0 pa-0"></v-switch>
        </div>
        <SubtitlesConfig :mountSubtitlesEdit="subtitlesEditMounted" />
      </v-col>
      <v-col cols="12" lg="8">
        <div
          class="d-flex justify-center align-center wrapper"
          ref="outputCanvasWrapper"
          @click.self="editStore.cropping ? null : selectLayerId(null)"
        >
          <canvas v-show="!editStore.cropping" ref="outputAmbientShadowCanvas" class="ambient-shadow"></canvas>
          <canvas v-show="!editStore.cropping" ref="outputCanvas" style="background-color: black"></canvas>
          <canvas v-show="!editStore.cropping" id="layersCanvas"></canvas>
          <canvas
            v-show="!editStore.cropping && editStore.enableSubtitles"
            ref="outputSubtitlesCanvas"
            style="pointer-events: none"
          ></canvas>
          <v-img v-show="editStore.cropping" max-width="720" :src="clipperImageUrl" class="ambient-shadow"></v-img>
          <clipper-basic
            v-show="editStore.cropping"
            ref="clipper"
            :src="clipperImageUrl"
            :border="0"
            :grid="false"
            @load="setClipperCrop"
            style="max-width: 720px"
          ></clipper-basic>
        </div>

        <v-col
          cols="12"
          class="d-flex justify-center align-center controls-wrapper mt-3"
          @click.self="editStore.cropping ? null : selectLayerId(null)"
        >
          <div class="player-controls">
            <v-btn icon depressed rounded small @click="seek(Math.max(0, currentTime - 2.5), true)" class="mr-2">
              <v-icon>mdi-skip-backward</v-icon>
            </v-btn>
            <v-btn icon depressed rounded small @click="toggleVideo()" :disabled="editStore.cropping" class="mr-2">
              <v-icon>{{ isPaused ? "mdi-play" : "mdi-pause" }}</v-icon>
            </v-btn>
            <v-btn icon depressed rounded small @click="seek(Math.min(duration, currentTime + 2.5), true)">
              <v-icon>mdi-skip-forward</v-icon>
            </v-btn>
          </div>

          <EditorControls v-if="!$vuetify.breakpoint.xs" />
        </v-col>
        <v-col cols="12">
          <Timeline
            v-if="timelineMounted"
            ref="timeline"
            :duration="duration"
            :currentTime="currentTime"
            :isPlaying="!isPaused"
            :suggestedShortDuration="shortStore.short.duration"
            :layers="
              layersCanvas
                .getObjects()
                .reduce(
                  (acc, e) => (e.metadata && e.metadata.id.startsWith('layer-') ? acc.concat(e.metadata) : acc),
                  []
                )
            "
            :selectedLayerId="editStore.selectedLayer ? editStore.selectedLayer.metadata.id : null"
            @seek="seek($event)"
            @set-layer-start="setLayerStart($event.layerId, $event.start)"
            @set-layer-end="setLayerEnd($event.layerId, $event.end)"
            @select-layer-id="selectLayerId($event)"
          />

          <span
            v-if="$vuetify.breakpoint.xs && editStore.faceDetecting && isFirstAutoCrop"
            class="d-block caption text--disabled text-center mt-4"
            >{{ $t("autoCropLoadingPercentage", { percentage: editStore.progress }) }}</span
          >
        </v-col>
      </v-col>
    </v-row>
    <SubtitlesPresets v-if="subtitlesPresetsMounted" />
    <BrandAssets v-if="editStore.brandAssetsMounted" />

    <v-snackbar
      v-model="snackbarStore.exportUpgradeSnackbar"
      :timeout="-1"
      bottom
      transition="slide-y-reverse-transition"
    >
      {{ $t("wantFasterExports") }}
      <template v-slot:action="{ attrs }">
        <v-btn text rounded v-bind="attrs" @click="snackbarStore.exportUpgradeSnackbar = false; upgradeStore.open($t('wantFasterExports'), $t('timeToGiveYourAccountBoost'), `${$t('enjoyExportSpeedsUnderMinute')} ${$t('forFasterExportingPleaseUpgradeToLite')}`)">
          {{ $t("upgrade") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import BrandAssets from "@/components/edit/BrandAssets.vue";
import SubtitlesPresets from "@/components/edit/SubtitlesPresets.vue";
import Timeline from "@/components/edit/Timeline.vue";
import SubtitlesConfig from "@/components/edit/subtitles/SubtitlesConfig.vue";
import EditorControls from "@/components/edit/controls/EditorControls.vue";
import { useAccountStore } from "@/stores/account";
import { useBrandAssetStore } from "@/stores/brand-asset";
import { useEditStore } from "@/stores/edit";
import { useFeedbackStore } from "@/stores/feedback";
import { useShortStore } from "@/stores/short";
import { useSnackbarStore } from "@/stores/snackbar";
import { useSubtitlesPresetStore } from "@/stores/subtitles-preset";
import { useUpgradeStore } from "@/stores/upgrade";
import { SubtitleRender } from "@/util/Subtitle";
import { debounce, deepCopy } from "@/util/helpers";
import { fabric } from "fabric";
import { AlignGuidelines } from "fabric-guideline-plugin";
import Hammer from "hammerjs";
import { nanoid } from "nanoid";
import { clipperBasic } from "vuejs-clipper";
import { CLIPS_API_URL, CLIPS_SECONDARY_API_URL } from "../api.js";

export default {
  name: "EditView",
  components: {
    Timeline,
    SubtitlesPresets,
    SubtitlesConfig,
    EditorControls,
    BrandAssets,
    clipperBasic,
  },
  setup() {
    const accountStore = useAccountStore();
    const shortStore = useShortStore();
    const editStore = useEditStore();
    const snackbarStore = useSnackbarStore();
    const feedbackStore = useFeedbackStore();
    const subtitlesPresetStore = useSubtitlesPresetStore();
    const brandAssetStore = useBrandAssetStore();
    const upgradeStore = useUpgradeStore();
    return {
      accountStore,
      shortStore,
      editStore,
      snackbarStore,
      feedbackStore,
      subtitlesPresetStore,
      brandAssetStore,
      upgradeStore
    };
  },
  data() {
    return {
      loading: true,
      cardLoading: true,
      loadingHeading: this.$t("loading"),
      loadingDescription: "",
      duration: null,
      currentTime: 0,
      clipSourceUrl: null,
      clipSourceWidth: null,
      clipSourceHeight: null,
      layersCanvas: null,
      requestAnimationFrameId: null,
      isPaused: true,
      windowLastWidth: null,
      timelineMounted: false,
      subtitlesPresetsMounted: false,
      subtitlesEditMounted: false,
      brandAssetOverlay: null,
      brandAssetOverlayImage: null,
      builtInBrandAssets: [
        {
          value: null,
          text: this.$t("noBrandPreset"),
        },
      ],
      faceDetectionUrl: null,
      minFaceDetectionConfidence: 0.3,
      autoCropPadding: 75,
      faceDetectionPresignedPost: null,
      exportRequestToken: null,
      autoCropRequestToken: null,
      clipperImageUrl: null,
      disableBackgroundBlur: false,
      backgroundBlurSize: 16,
      backgroundBlurOpacity: 0.75,
      blurWatermarkImage: null,
      firstOutputAmbientShadowCanvasImageDrawed: false,
      isSafari: !!window.GestureEvent,
      isFirstAutoCrop: true,
    };
  },
  async mounted() {
    try {
      const resp = await this.shortStore.fetchShort(this.$route.params.shortId);
      this.exportRequestToken = resp.data.exportRequestToken;
      this.editStore.canExport = !!this.exportRequestToken;
      const clipRequestToken = resp.data.clipRequestToken;

      const setSseHandlers = (url) => {
        let sse = new EventSource(url);

        sse.onmessage = (e) => {
          const data = JSON.parse(e.data);
          const eventName = data.event;

          switch (eventName) {
            case "video-trimming-started":
              this.loadingHeading = this.$t("trimmingVideo");
              this.loadingDescription = this.$t("trimmingShouldTakeLessThanMinute");
              break;
            case "video-trimming-progress":
              this.loadingHeading = this.$t("trimmingVideoProgress", { progress: data.progress });
              break;
            case "merging-started":
              this.loadingHeading = this.$t("mergingVideoAndAudio");
              this.loadingDescription = null;
              break;
            case "clip-uploading-started":
              this.loadingHeading = this.$t("uploadingVideo");
              break;
            case "clip-url":
              this.clipSourceUrl = data.clipUrl;
              this.loadingHeading = this.$t("almostDone");
              this.loadingDescription = this.$t("retrievingYourVideoForEditing");
              break;
            case "face-detection-presigned-post":
              sse.close();
              this.faceDetectionPresignedPost = data.faceDetectionPresignedPost;
              this.faceDetectionUrl = data.faceDetectionUrl;
              this.autoCropRequestToken = data.autoCropRequestToken;
              // this.autoCrop();
              this.initEditor();
              break;
            case "face-detection-url":
              sse.close();
              this.faceDetectionUrl = data.faceDetectionUrl;
              // this.autoCrop();
              this.initEditor();
              break;
            case "error":
              sse.close();
              this.cardLoading = false;
              this.loadingHeading = this.$t("somethingWentWrong");
              this.loadingDescription = this.$t("tryAgainLaterOrContactSupport");
              switch (data.error) {
                case "YOUTUBE_VIDEO_UNAVAILABLE":
                  this.loadingDescription = this.$t("youtubeVideoUnavailable");
                  break;
                case "YOUTUBE_PREMIERS_NOT_SUPPORTED":
                  this.loadingDescription = this.$t("youtubePremieresNotSupported");
                  break;
                case "YOUTUBE_AGE_RESTRICTED_VIDEOS_NOT_SUPPORTED":
                  this.loadingDescription = this.$t("youtubeAgeRestrictedVideosNotSupported");
                  break;
                case "YOUTUBE_PRIVATE_VIDEOS_NOT_SUPPORTED":
                  this.loadingDescription = this.$t("youtubePrivateVideosNotSupported");
                  break;
                case "YOUTUBE_VIDEO_BEING_PROCESSED":
                  this.loadingDescription = this.$t("youtubeVideoStillBeingProcessed");
                  break;
              }
          }
        };

        sse.onerror = () => {
          sse.close();

          if (url !== `${CLIPS_API_URL}?token=${clipRequestToken}`)
            setSseHandlers(`${CLIPS_API_URL}?token=${clipRequestToken}`);
          else {
            this.cardLoading = false;
            this.loadingHeading = this.$t("somethingWentWrong");
            this.loadingDescription = this.$t("tryAgainLaterOrContactSupport");
          }
        };
      };

      setSseHandlers(`${CLIPS_SECONDARY_API_URL}?token=${clipRequestToken}`);
    } catch {
      this.cardLoading = false;
      this.loadingHeading = this.$t("somethingWentWrong");
      this.loadingDescription = this.$t("tryAgainLaterOrContactSupport");
    }
  },
  methods: {
    initEditor() {
      this.loading = false;
      window.addEventListener("resize", debounce(this.resizeCanvas, 250));

      const video = document.createElement("video");

      video.preload = "metadata";
      video.playsInline = true;
      video.loop = true;
      video.crossOrigin = "anonymous";
      video.autoplay = true;
      video.src = this.clipSourceUrl;

      // Adding Event Listeners
      video.addEventListener("loadedmetadata", this.init);
      video.addEventListener("loadeddata", this.videoLoadedData);
      video.addEventListener("progress", this.videoProgress);
      video.addEventListener("play", () => {
        this.isPaused = false;
        this.animateCanvas();
      });
      video.addEventListener("timeupdate", (event) => {
        this.currentTime = event.target.currentTime;
        this.hideNotCurrentLayers();
      });
      video.addEventListener("pause", () => {
        this.isPaused = true;
      });

      this.$refs.inputVideo = video;
    },
    init(e) {
      const video = this.$refs.inputVideo;
      this.clipSourceWidth = e.target.videoWidth;
      this.clipSourceHeight = e.target.videoHeight;
      this.duration = video.duration;

      const canvas = new fabric.Canvas("layersCanvas", {
        preserveObjectStacking: false,
      });

      canvas.selection = false;

      canvas.on("object:moving", (e) => {
        const obj = e.target;
        obj.setCoords();

        if (obj.getBoundingRect().top < 0 || obj.getBoundingRect().left < 0) {
          obj.set("top", Math.max(obj.top, obj.top - obj.getBoundingRect().top));
          obj.set("left", Math.max(obj.left, obj.left - obj.getBoundingRect().left));
        }
        if (
          obj.getBoundingRect().top + obj.getBoundingRect().height > obj.canvas.height ||
          obj.getBoundingRect().left + obj.getBoundingRect().width > obj.canvas.width
        ) {
          obj.set(
            "top",
            Math.min(obj.top, obj.canvas.height - obj.getBoundingRect().height + obj.top - obj.getBoundingRect().top)
          );
          obj.set(
            "left",
            Math.min(obj.left, obj.canvas.width - obj.getBoundingRect().width + obj.left - obj.getBoundingRect().left)
          );
        }
      });

      canvas.on("object:scaling", (e) => {
        const obj = e.target;
        if (obj.metadata.id.startsWith("remote-layer-")) return;
        obj.setCoords();

        // Calculate the maximum scales based on the ratio of the canvas dimensions to the object's dimensions
        const maxScaleX = (canvas.width / (obj.width * obj.scaleX)) * obj.scaleX;
        const maxScaleY = (canvas.height / (obj.height * obj.scaleY)) * obj.scaleY;

        // Adjust the scale only if it exceeds the maximum allowed to fit the canvas
        if (obj.scaleX > maxScaleX || obj.scaleY > maxScaleY) {
          const adjustedScaleX = Math.min(obj.scaleX, maxScaleX);
          const adjustedScaleY = Math.min(obj.scaleY, maxScaleY);

          obj.set({
            scaleX: adjustedScaleX,
            scaleY: adjustedScaleY,
          });
        }

        // Keep track of the object's boundary to adjust its position if it's going out of the canvas
        const br = obj.getBoundingRect(true);

        // Ensure the object remains within the canvas boundaries
        if (br.left < 0 || br.top < 0 || br.left + br.width > canvas.width || br.top + br.height > canvas.height) {
          obj.set({
            left: Math.min(Math.max(0, obj.left), canvas.width - obj.getScaledWidth()),
            top: Math.min(Math.max(0, obj.top), canvas.height - obj.getScaledHeight()),
          });
        }
      });

      canvas.on("object:scaling", (e) => {
        const obj = e.target;
        if (obj.metadata.id.startsWith("layer-")) return;
        obj.setCoords();

        // Calculate the maximum scales based on the ratio of the canvas dimensions to the object's dimensions
        const maxScaleX = (canvas.width / (obj.width * obj.scaleX)) * obj.scaleX;
        const maxScaleY = (canvas.height / (obj.height * obj.scaleY)) * obj.scaleY;

        // Adjust the scale only if it exceeds the maximum allowed to fit the canvas
        if (obj.scaleX > maxScaleX || obj.scaleY > maxScaleY) {
          const adjustedScaleX = Math.min(obj.scaleX, maxScaleX);
          const adjustedScaleY = Math.min(obj.scaleY, maxScaleY);

          obj.set({
            scaleX: adjustedScaleX,
            scaleY: adjustedScaleY,
          });
        }

        // fix scaling and stretching issues by @amit
        // Aspect ratio should be same. So Scaling should be same on both axis
        let isAnyAxisOverScaled = obj.scaleX != obj.scaleY;
        if (isAnyAxisOverScaled) {
          // go back to minimum scaled axis
          let scale = Math.min(obj.scaleX, obj.scaleY);
          obj.set({
            scaleX: scale,
            scaleY: scale,
          });
        }
        // fix end

        // Keep track of the object's boundary to adjust its position if it's going out of the canvas
        const br = obj.getBoundingRect(true);

        // Ensure the object remains within the canvas boundaries
        if (br.left < 0 || br.top < 0 || br.left + br.width > canvas.width || br.top + br.height > canvas.height) {
          obj.set({
            left: Math.min(Math.max(0, obj.left), canvas.width - obj.getScaledWidth()),
            top: Math.min(Math.max(0, obj.top), canvas.height - obj.getScaledHeight()),
          });
        }
      });

      canvas.on("mouse:dblclick", (e) => {
        const obj = e.target;
        if (!obj || obj.metadata.id.startsWith("remote-layer-")) return;

        const isFullScreen =
          obj.top === 0 && obj.left === 0 && obj.width === canvas.width && obj.height === canvas.height;

        obj.set({
          top: 0,
          left: 0,
          width: canvas.width,
          height: isFullScreen ? canvas.height / 2 : canvas.height,
          scaleX: 1,
          scaleY: 1,
        });

        canvas.requestRenderAll();
      });

      canvas.on("selection:cleared", () => {
        if (!this.editStore.cropping) this.editStore.selectedLayer = null;
      });

      this.layersCanvas = canvas;

      this.resizeCanvas();
      this.timelineMounted = true;

      const guidelineHelperRect = new fabric.Rect({
        top: 0,
        left: 0,
        width: this.layersCanvas.width,
        height: this.layersCanvas.height,
        visible: false,
        isGuidelineHelperRect: true,
        metadata: { id: "guidelineHelperRect" },
      });

      this.layersCanvas.add(guidelineHelperRect);

      const guideline = new AlignGuidelines({
        canvas: this.layersCanvas,
        pickObjTypes: [
          { key: "isGuidelineHelperRect", value: true },
          { key: "type", value: "rect" },
        ],
        aligningOptions: {
          lineColor: "#0169EE",
          lineWidth: 0,
        },
      });

      guideline.init();

      this.addLayer(0, 0, 0, 0, 0, 0, 0, 0, [{ start: 0, end: 0 }], false);

      this.$refs.clipper.onChange$.subscribe(() => {
        if (this.editStore.cropping) this.debouncedSetLayerCrop();
      });
    },
    videoLoadedData() {
      this.editStore.initialized = true;

      this.fetchShortTranscript();
      if (!this.$vuetify.breakpoint.xs) this.fetchSubtitlesPresets();

      this.renderCanvas();
      this.autoCrop();
    },
    videoProgress() {
      if (this.isSafari) {
        if (!this.editStore.initialized) {
          this.editStore.initialized = true;
          // this.autoCrop();
        }
      }
    },
    seek(time, keepPlaying = false) {
      const wasPlaying = !this.isPaused;
      if (wasPlaying) this.pauseVideo();
      this.currentTime = time;
      const video = this.$refs.inputVideo;
      video.currentTime = time;

      if (wasPlaying && keepPlaying) this.playVideo();

      if (this.editStore.cropping) this.debouncedInitLayerCrop();
    },
    resizeCanvas(force = false) {
      try {
        if (!force && window.innerWidth === this.windowLastWidth) return;
        const outputCanvasWrapper = this.$refs.outputCanvasWrapper;
        const outputCanvasWrapperWidth = Math.round(outputCanvasWrapper.clientWidth);
        const outputCanvasWrapperHeight = Math.round(outputCanvasWrapper.clientHeight);
        const outputCanvasElement = this.$refs.outputCanvas;
        const outputAmbientShadowCanvas = this.$refs.outputAmbientShadowCanvas;
        const outputSubtitlesCanvas = this.$refs.outputSubtitlesCanvas;

        let ratio; // Height based ratio!
        switch (this.editStore.aspectRatio) {
          case "16:9":
            ratio = 16 / 9;
            break;
          case "1:1":
            ratio = 1;
            break;
          case "9:16":
            ratio = 9 / 16;
            break;
          default:
            throw new Error("Invalid aspect ratio");
        }

        let newHeight = outputCanvasWrapperHeight;
        let newWidth = Math.round(newHeight * ratio);

        if (newWidth > outputCanvasWrapperWidth) {
          newWidth = outputCanvasWrapperWidth;

          switch (this.editStore.aspectRatio) {
            case "16:9":
              ratio = 9 / 16;
              break;
            case "9:16":
              ratio = 16 / 9;
              break;
          }

          newHeight = Math.round(newWidth * ratio);
        }

        outputCanvasElement.width = newWidth;
        outputCanvasElement.height = newHeight;

        outputAmbientShadowCanvas.width = newWidth;
        outputAmbientShadowCanvas.height = newHeight;
        this.firstOutputAmbientShadowCanvasImageDrawed = false;

        outputSubtitlesCanvas.width = newWidth;
        outputSubtitlesCanvas.height = newHeight;

        this.layersCanvas.setWidth(newWidth);
        this.layersCanvas.setHeight(newHeight);

        const guidelineHelperRect = this.layersCanvas
          .getObjects()
          .find((e) => e.metadata.id.startsWith("guidelineHelperRect"));
        if (guidelineHelperRect)
          guidelineHelperRect.set({
            width: newWidth,
            height: newHeight,
            scaleX: 1,
            scaleY: 1,
          });

        this.layersCanvas.requestRenderAll();

        // if (outputCanvasElementWidth !== outputCanvasWrapperWidth) {
        //   this.resetBoxes();
        //   this.addLayer();
        // }

        this.windowLastWidth = window.innerWidth;
      } catch {
        return;
      }
    },
    animateCanvas() {
      const step = () => {
        this.renderCanvas();
        this.requestAnimationFrameId = window.requestAnimationFrame(step);
      };
      this.requestAnimationFrameId = window.requestAnimationFrame(step);
    },
    cancelCanvasAnimation() {
      window.cancelAnimationFrame(this.requestAnimationFrameId);
    },
    toggleVideo() {
      const video = this.$refs.inputVideo;
      if (video.paused) video.play();
      else video.pause();
    },
    playVideo() {
      const video = this.$refs.inputVideo;
      video.play();
    },
    pauseVideo() {
      const video = this.$refs.inputVideo;
      video.pause();
    },
    addLayer(
      top = 0,
      left = 0,
      width = 0,
      height = 0,
      sourceCropTop = 0,
      sourceCropLeft = 0,
      sourceCropWidth = 0,
      sourceCropHeight = 0,
      enableBetween = [{ start: 0, end: 0 }],
      select = true
    ) {
      const id = `layer-${nanoid(8)}`;

      if (width === 0) width = this.layersCanvas.width;
      if (height === 0) height = this.layersCanvas.height;
      if (sourceCropWidth === 0) sourceCropWidth = this.clipSourceWidth;
      if (sourceCropHeight === 0) sourceCropHeight = this.clipSourceHeight;

      const rect = new fabric.Rect({
        top,
        left,
        width,
        height,
        lockRotation: true,
        lockUniScaling: true,
        transparentCorners: false,
        borderColor: "#FFF",
        cornerColor: "#FFF",
        fill: "transparent",
        padding: 0,
        visible: true,
        cornerStyle: "circle",
        // centeredScaling: true,
        // lockMovementX: true,
        // lockMovementY: true,
        metadata: {
          id,
          enableBetween,
          sourceCrop: {
            top: sourceCropTop,
            left: sourceCropLeft,
            width: sourceCropWidth,
            height: sourceCropHeight,
          },
        },
      });

      rect.setControlsVisibility({
        mtr: false,
      });

      this.layersCanvas.add(rect);

      rect.on("selected", (e) => {
        this.editStore.selectedLayer = e.target;
      });

      // Prevent default touch action to avoid scrolling and zooming
      this.layersCanvas.on("mouse:down", (opt) => {
        const evt = opt.e;
        if (evt.type === "touchstart") {
          evt.preventDefault();
        }
      });

      // Initialize Hammer on the canvas
      const mc = new Hammer(this.layersCanvas.upperCanvasEl);

      mc.get("pinch").set({ enable: true });

      let initialScale = 1;
      let activeObject = null;

      mc.on("pinchstart", () => {
        activeObject = this.layersCanvas.getActiveObject();
        if (activeObject && !activeObject.metadata.id.startsWith("remote-layer-")) {
          initialScale = activeObject.scaleX;
        }
      });

      mc.on("pinch", (e) => {
        if (activeObject && !activeObject.metadata.id.startsWith("remote-layer-")) {
          let newScale = initialScale * e.scale;

          const maxWidth = this.layersCanvas.width - activeObject.left;
          const maxHeight = this.layersCanvas.height - activeObject.top;

          const maxScaleX = Math.max(
            maxWidth / activeObject.width,
            maxWidth / (activeObject.width * activeObject.scaleX)
          );
          const maxScaleY = Math.max(
            maxHeight / activeObject.height,
            maxHeight / (activeObject.height * activeObject.scaleY)
          );

          const minAllowedScale = Math.min(maxScaleX, maxScaleY, newScale);

          activeObject.scale(minAllowedScale);

          this.layersCanvas.requestRenderAll();
        }
      });

      mc.on("pinchend", () => {
        activeObject = null;
      });

      if (select) this.selectLayerId(id);
    },
    addRemoteLayer(
      url,
      top = 0,
      left = 0,
      width = 0,
      height = 0,
      enableBetween = [{ start: 0, end: 0 }],
      select = true
    ) {
      if (width === 0) width = this.layersCanvas.width;
      if (height === 0) height = this.layersCanvas.height;

      const id = `remote-layer-${nanoid(8)}`;
      fabric.Image.fromURL(
        url,
        (remoteImage) => {
          let scaleX = 1;
          let scaleY = 1;

          // Calculate aspect ratios
          const canvasAspectRatio = this.layersCanvas.width / this.layersCanvas.height;
          const imageAspectRatio = remoteImage.width / remoteImage.height;

          // Adjust image to fit inside the canvas
          if (imageAspectRatio > canvasAspectRatio) {
            // Image is wider than canvas
            scaleX = this.layersCanvas.width / remoteImage.width;
            scaleY = scaleX; // maintain aspect ratio
          } else {
            // Image is taller or same aspect ratio as canvas
            scaleY = this.layersCanvas.height / remoteImage.height;
            scaleX = scaleY; // maintain aspect ratio
          }

          remoteImage.set({ scaleX, scaleY });

          remoteImage.setControlsVisibility({
            mtr: false,
            mb: false,
            ml: false,
            mr: false,
            mt: false,
          });

          this.layersCanvas.add(remoteImage);

          remoteImage.on("selected", (e) => {
            this.editStore.selectedLayer = e.target;
          });

          if (select) this.selectLayerId(id);
        },
        {
          crossOrigin: "anonymous",
          top,
          left,
          lockRotation: true,
          lockUniScaling: true,
          transparentCorners: false,
          borderColor: "#FFF",
          cornerColor: "#FFF",
          padding: 0,
          visible: true,
          cornerStyle: "circle",
          metadata: {
            id,
            url,
            enableBetween,
          },
        }
      );
    },
    setLayerStart(layerId, start, cutIndex = 0) {
      const layer = this.layersCanvas.getObjects().find((e) => e.metadata.id === layerId);
      if (layer) layer.metadata.enableBetween[cutIndex].start = start;
    },
    setLayerEnd(layerId, end, cutIndex = 0) {
      const layer = this.layersCanvas.getObjects().find((e) => e.metadata.id === layerId);
      if (layer) layer.metadata.enableBetween[cutIndex].end = end;
    },
    removeLayer(layerId) {
      const layer = this.layersCanvas.getObjects().find((e) => e.metadata.id === layerId);
      if (layer) this.layersCanvas.remove(layer);
    },
    calculateUnionArea(rectangles) {
      let totalArea = 0;
      let xCoords = [];
      let yCoords = [];

      rectangles.forEach((rect) => {
        xCoords.push(rect.x, rect.x + rect.width);
        yCoords.push(rect.y, rect.y + rect.height);
      });

      xCoords = Array.from(new Set(xCoords)).sort((a, b) => a - b);
      yCoords = Array.from(new Set(yCoords)).sort((a, b) => a - b);

      for (let i = 0; i < xCoords.length - 1; i++) {
        for (let j = 0; j < yCoords.length - 1; j++) {
          const width = xCoords[i + 1] - xCoords[i];
          const height = yCoords[j + 1] - yCoords[j];

          if (
            rectangles.some(
              (rect) =>
                rect.x < xCoords[i + 1] &&
                rect.x + rect.width > xCoords[i] &&
                rect.y < yCoords[j + 1] &&
                rect.y + rect.height > yCoords[j]
            )
          ) {
            totalArea += width * height;
          }
        }
      }

      return totalArea;
    },
    renderCanvas() {
      if (this.editStore.cropping) return;
      const drawImageCommands = [];
      const inputVideoElement = this.$refs.inputVideo;
      const outputCanvasElement = this.$refs.outputCanvas;
      const outputCanvasContext = outputCanvasElement.getContext("2d");
      const subtitlesCanvasElement = this.$refs.outputSubtitlesCanvas;
      const subtitlesCanvasContext = subtitlesCanvasElement.getContext("2d");
      const outputAmbientShadowCanvasElement = this.$refs.outputAmbientShadowCanvas;
      const outputAmbientShadowCanvasContext = outputAmbientShadowCanvasElement.getContext("2d");

      outputCanvasContext.clearRect(0, 0, outputCanvasElement.width, outputCanvasElement.height);

      const layersDrawImageCommands = [];
      const layers = this.layersCanvas.getObjects().filter((e) => e.visible && e.metadata.id.startsWith("layer-"));

      let activeLayerRects = [];

      for (let index = 0; index < layers.length; index++) {
        const layer = layers[index];
        const layerStart = layer.metadata.enableBetween[0].start;
        const layerEnd = layer.metadata.enableBetween[0].end;
        if ((!layerStart || this.currentTime >= layerStart) && (!layerEnd || this.currentTime <= layerEnd)) {
          const x = layer.metadata.sourceCrop.left;
          const y = layer.metadata.sourceCrop.top;
          const width = layer.metadata.sourceCrop.width;
          const height = layer.metadata.sourceCrop.height;

          const outputScale = outputCanvasElement.width / outputCanvasElement.offsetWidth;
          const outputX = layer.left * outputScale;
          const outputY = layer.top * outputScale;
          const outputWidth = layer.getScaledWidth() * outputScale;
          const outputHeight = layer.getScaledHeight() * outputScale;

          activeLayerRects.push({
            x: outputX,
            y: outputY,
            width: outputWidth,
            height: outputHeight,
          });

          const sourceAspectRatio = width / height;
          const targetAspectRatio = outputWidth / outputHeight;

          let sourceX = x;
          let sourceY = y;
          let sourceWidth = width;
          let sourceHeight = height;

          if (sourceAspectRatio > targetAspectRatio) {
            // source is wider than target
            sourceWidth = height * targetAspectRatio;
            sourceX = x + (width - sourceWidth) / 2;
          } else {
            // source is taller than target
            sourceHeight = width / targetAspectRatio;
            sourceY = y + (height - sourceHeight) / 2;
          }

          layersDrawImageCommands.push([
            inputVideoElement,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            outputX,
            outputY,
            outputWidth,
            outputHeight,
          ]);
        }
      }

      if (layersDrawImageCommands.length > 0) {
        const totalCanvasArea = outputCanvasElement.width * outputCanvasElement.height;

        if (!this.disableBackgroundBlur && this.calculateUnionArea(activeLayerRects) <= totalCanvasArea * 0.975) {
          const tempCanvas = document.createElement("canvas");
          const tempContext = tempCanvas.getContext("2d");

          // Set tempCanvas size to match outputCanvasElement
          tempCanvas.width = outputCanvasElement.width;
          tempCanvas.height = outputCanvasElement.height;

          // Parameters from the source video
          let sourceX = 0;
          let sourceY = 0;
          let sourceWidth = inputVideoElement.videoWidth;
          let sourceHeight = inputVideoElement.videoHeight;

          // Parameters for the target canvas
          let outputX = 0;
          let outputY = 0;
          let outputWidth = tempCanvas.width;
          let outputHeight = tempCanvas.height;

          const sourceAspectRatio = sourceWidth / sourceHeight;
          const targetAspectRatio = outputWidth / outputHeight;

          if (sourceAspectRatio > targetAspectRatio) {
            // Source is wider than target
            sourceWidth = sourceHeight * targetAspectRatio;
            sourceX = (inputVideoElement.videoWidth - sourceWidth) / 2;
          } else {
            // Source is taller than target
            sourceHeight = sourceWidth / targetAspectRatio;
            sourceY = (inputVideoElement.videoHeight - sourceHeight) / 2;
          }

          let sourceElement = inputVideoElement;
          if (this.isSafari) {
            if (this.blurWatermarkImage === null) {
              this.blurWatermarkImage = new Image();
              this.blurWatermarkImage.src = "/img/blur-watermark.png";
            } else if (this.blurWatermarkImage.complete) sourceElement = this.blurWatermarkImage;
          }

          // Apply the blur filter and draw the image
          if (!this.isSafari)
            tempContext.filter = `brightness(${this.backgroundBlurOpacity}) blur(${this.backgroundBlurSize}px)`;
          tempContext.drawImage(
            sourceElement,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            outputX,
            outputY,
            outputWidth,
            outputHeight
          );

          // Draw the blurred image from the temporary canvas onto the output canvas
          drawImageCommands.push([tempCanvas, 0, 0, outputCanvasElement.width, outputCanvasElement.height]);
        }

        drawImageCommands.push(...layersDrawImageCommands);
      }

      for (let i = 0; i < drawImageCommands.length; i++) {
        outputCanvasContext.drawImage(...drawImageCommands[i]);
        if (!this.isSafari || !this.firstOutputAmbientShadowCanvasImageDrawed) {
          outputAmbientShadowCanvasContext.drawImage(...drawImageCommands[i]);
          this.firstOutputAmbientShadowCanvasImageDrawed = true;
        }
      }

      if (!this.editStore.enableSubtitles) return;
      const currentTime = this.$refs.inputVideo.currentTime;

      function getSettings(aspectRatio, blockVerticalPosition) {
        if (aspectRatio === "16:9") {
          return {
            originalCanvasSize: {
              width: 1920,
              height: 1080,
            },
            padding: 100,
            yTop: {
              auto: 660,
              top: 50,
              middle: 0, // middle is handled at time of rendering
              bottom: 660,
            }[blockVerticalPosition],
            maxWidth: 1920 - 100 - 100,
          };
        } else if (aspectRatio === "9:16") {
          return {
            originalCanvasSize: {
              width: 1080,
              height: 1920,
            },
            padding: 100,
            yTop: {
              auto: 1500,
              top: 50,
              middle: 0, // middle is handled at time of rendering
              bottom: 1500,
            }[blockVerticalPosition],
            maxWidth: 1080 - 100 - 100,
          };
        } else if (aspectRatio === "1:1") {
          return {
            originalCanvasSize: {
              width: 1080,
              height: 1080,
            },
            padding: 100,
            yTop: {
              auto: 660,
              top: 50,
              middle: 0, // middle is handled at time of rendering
              bottom: 660,
            }[blockVerticalPosition],
            maxWidth: 1080 - 100 - 100,
          };
        }
      }

      const settings = getSettings(this.editStore.aspectRatio, this.editStore.subtitlesConfig.blockVerticalPosition);
      const currentCanvasSize = {
        width: outputCanvasElement.width,
        height: outputCanvasElement.height,
      };
      const scale = currentCanvasSize.width / settings.originalCanvasSize.width;

      // Todo: Check everything with respect to scale;
      const playerConfig = {
        maxWidth: settings.maxWidth,
        currentTime: currentTime,
        lineHeight: 120,
        blockPositionPaddingFactorTop: 0.15,
        blockPositionPaddingFactorBottom: 0.15,
        x: settings.padding,
        canvasWidth: settings.originalCanvasSize.width,
        canvasHeight: settings.originalCanvasSize.height,
        context: subtitlesCanvasContext,
        emojiFontStyle: "Noto Color Emoji",
      };
      // scale according to player resize;
      subtitlesCanvasContext.clearRect(0, 0, currentCanvasSize.width, currentCanvasSize.height);
      subtitlesCanvasContext.save();
      subtitlesCanvasContext.scale(scale, scale);
      this.renderSubtitles(
        this.editStore.subtitlesConfig,
        playerConfig,
        this.shortStore.editedShortTranscriptWords || []
      );
      subtitlesCanvasContext.restore();
    },
    renderSubtitles(subtitlesConfig, playerConfig, transcriptionByWords) {
      playerConfig.context.save();
      const currentTranscript = SubtitleRender.GetCurrentTranspcript(
        subtitlesConfig,
        playerConfig,
        transcriptionByWords
      );
      if (currentTranscript) {
        playerConfig.context.lineWidth = subtitlesConfig.font.fontStrokeSize;
        const fontWeight = subtitlesConfig.font.fontWeight === 700 ? "bold" : "normal";
        playerConfig.context.font = `${fontWeight} ${subtitlesConfig.font.fontSize}px ${subtitlesConfig.font.fontName}, ${playerConfig.emojiFontStyle}`;
        playerConfig.context.fillStyle = "white";
        playerConfig.context.lineJoin = "round";
        let currentTranscriptLines = SubtitleRender.BreakIntoLines(subtitlesConfig, playerConfig, currentTranscript);
        SubtitleRender.Draw(subtitlesConfig, playerConfig, currentTranscriptLines);
      }
      playerConfig.context.restore();
    },
    resetLayers() {
      const remoteLayersBackup = this.layersCanvas
        .getObjects()
        .filter((e) => e.metadata.id.startsWith("remote-layer-"));

      this.layersCanvas.clear();

      const guidelineHelperRect = new fabric.Rect({
        top: 0,
        left: 0,
        width: this.layersCanvas.width,
        height: this.layersCanvas.height,
        visible: false,
        isGuidelineHelperRect: true,
        metadata: { id: "guidelineHelperRect" },
      });

      this.layersCanvas.add(guidelineHelperRect);

      remoteLayersBackup.forEach((e) => {
        this.layersCanvas.add(e);
      });
    },
    bringRemoteLayersToFront() {
      const remoteLayers = this.layersCanvas.getObjects().filter((e) => e.metadata.id.startsWith("remote-layer-"));

      remoteLayers.forEach((e) => {
        this.layersCanvas.bringToFront(e);
      });
    },

    async getOptions(ignoreSubtitles = false, isExport = true) {
      const outputCanvasElement = this.$refs.outputCanvas;
      const inputVideoElement = this.$refs.inputVideo;
      const clipOutputWidth = this.editStore.aspectRatio === "16:9" ? 1920 : 1080;
      const clipOutputHeight = this.editStore.aspectRatio === "9:16" ? 1920 : 1080;
      const scale = clipOutputWidth / outputCanvasElement.width;

      // Todo: change the subtitles behaviour based on isExport param (when exporting, return words; when saving return edited transcript) (future release)
      let subtitlesConfig;
      let subtitlesLanguage;
      let transcriptWords;
      if (!ignoreSubtitles && this.editStore.enableSubtitles) {
        subtitlesConfig = this.editStore.subtitlesConfig;
        subtitlesLanguage = this.shortStore.shortTranscript.transcriptLanguage;
        transcriptWords = this.shortStore.editedShortTranscriptWords;
      }

      const o = {
        outputName: `${
          this.shortStore.short.title
            ? this.shortStore.short.title.toLowerCase().replaceAll(":", "").replaceAll(" ", "-")
            : this.shortId
        }.mp4`,
        inputWidth: this.clipSourceWidth,
        inputHeight: this.clipSourceHeight,
        inputDuration: inputVideoElement.duration,
        outputWidth: clipOutputWidth,
        outputHeight: clipOutputHeight,
        start: Infinity,
        end: 0,
        layers: [],
        remoteLayers: [],
        ...(!ignoreSubtitles && {
          enableSubtitles: this.editStore.enableSubtitles,
        }),
        ...(subtitlesConfig && { subtitlesConfig }),
        ...(subtitlesLanguage && { subtitlesLanguage }),
        ...(transcriptWords && { transcriptWords }),
      };

      // Layers
      const layers = this.layersCanvas.getObjects().filter((e) => e.metadata.id.startsWith("layer-"));
      for (let index = 0; index < layers.length; index++) {
        const layer = layers[index];

        // TODO: Modify the logic to support multiple enableBetween elements (future release)
        const layerStart = layer.metadata.enableBetween[0].start;
        const layerEnd = layer.metadata.enableBetween[0].end;
        if (layerStart < o.start) o.start = layerStart;
        if (layerEnd > o.end) o.end = layerEnd;

        const outputScale = outputCanvasElement.width / outputCanvasElement.offsetWidth;
        const outputX = layer.left * outputScale;
        const outputY = layer.top * outputScale;
        const outputWidth = layer.getScaledWidth() * outputScale;
        const outputHeight = layer.getScaledHeight() * outputScale;

        let sourceX = layer.metadata.sourceCrop.left;
        let sourceY = layer.metadata.sourceCrop.top;
        let sourceWidth = layer.metadata.sourceCrop.width;
        let sourceHeight = layer.metadata.sourceCrop.height;

        if (isExport) {
          const sourceAspectRatio = sourceWidth / sourceHeight;
          const targetAspectRatio = outputWidth / outputHeight;

          if (sourceAspectRatio > targetAspectRatio) {
            sourceWidth = layer.metadata.sourceCrop.height * targetAspectRatio;
            sourceX = layer.metadata.sourceCrop.left + (layer.metadata.sourceCrop.width - sourceWidth) / 2;
          } else {
            sourceHeight = layer.metadata.sourceCrop.width / targetAspectRatio;
            sourceY = layer.metadata.sourceCrop.top + (layer.metadata.sourceCrop.height - sourceHeight) / 2;
          }
        }

        o.layers.push({
          sourceWidth,
          sourceHeight,
          sourceX,
          sourceY,
          outputWidth: Math.min(outputWidth * scale, clipOutputWidth),
          outputHeight: Math.min(outputHeight * scale, clipOutputHeight),
          outputX: outputX * scale,
          outputY: outputY * scale,
          enableBetween: layer.metadata.enableBetween,
        });
      }

      // Background blur
      if (isExport && !this.disableBackgroundBlur) {
        const isBackgroundBlurEnabledAtTime = (layers, time, totalArea) => {
          const activeLayerRects = [];
          layers.forEach((layer) => {
            for (let range of layer.enableBetween) {
              if (time >= range.start && time <= range.end) {
                activeLayerRects.push({
                  x: layer.outputX,
                  y: layer.outputY,
                  width: layer.outputWidth,
                  height: layer.outputHeight,
                });
                break;
              }
            }
          });
          return this.calculateUnionArea(activeLayerRects) <= totalArea * 0.975;
        };

        let timestamps = [];
        o.layers.forEach((layer) => {
          layer.enableBetween.forEach((range) => {
            timestamps.push(range.start, range.end);
          });
        });

        timestamps = [...new Set(timestamps)].sort((a, b) => a - b);

        const totalArea = clipOutputWidth * clipOutputHeight;
        const backgroundBlurEnableBetween = [];
        let isCurrentlyEnabled = false;

        for (let i = 0; i < timestamps.length - 1; i++) {
          let checkTime = (timestamps[i] + timestamps[i + 1]) / 2;
          let shouldBeEnabled = isBackgroundBlurEnabledAtTime(o.layers, checkTime, totalArea);

          if (isCurrentlyEnabled !== shouldBeEnabled) {
            isCurrentlyEnabled = shouldBeEnabled;
            if (shouldBeEnabled) backgroundBlurEnableBetween.push({ start: timestamps[i] });
            else backgroundBlurEnableBetween[backgroundBlurEnableBetween.length - 1].end = timestamps[i];
          }
        }

        if (
          backgroundBlurEnableBetween.length > 0 &&
          typeof backgroundBlurEnableBetween[backgroundBlurEnableBetween.length - 1].end === "undefined"
        ) {
          backgroundBlurEnableBetween[backgroundBlurEnableBetween.length - 1].end = timestamps[timestamps.length - 1];
        }

        if (backgroundBlurEnableBetween.length > 0) {
          let sourceX = 0;
          let sourceY = 0;
          let sourceWidth = inputVideoElement.videoWidth;
          let sourceHeight = inputVideoElement.videoHeight;

          const sourceAspectRatio = sourceWidth / sourceHeight;
          const targetAspectRatio = clipOutputWidth / clipOutputHeight;

          if (sourceAspectRatio > targetAspectRatio) {
            sourceWidth = sourceHeight * targetAspectRatio;
            sourceX = (inputVideoElement.videoWidth - sourceWidth) / 2;
          } else {
            sourceHeight = sourceWidth / targetAspectRatio;
            sourceY = (inputVideoElement.videoHeight - sourceHeight) / 2;
          }

          o.layers.splice(0, 0, {
            sourceWidth: Math.min(sourceWidth, this.clipSourceWidth),
            sourceHeight: Math.min(sourceHeight, this.clipSourceHeight),
            sourceX,
            sourceY,
            outputWidth: 1080,
            outputHeight: 1920,
            outputX: 0,
            outputY: 0,
            enableBetween: backgroundBlurEnableBetween,
            opacity: this.backgroundBlurOpacity,
            blur: this.backgroundBlurSize,
          });
        }
      }

      // Remote layers (brand asset overlays)
      const remoteLayers = this.layersCanvas.getObjects().filter((e) => e.metadata.id.startsWith("remote-layer-"));
      for (let index = 0; index < remoteLayers.length; index++) {
        const layer = remoteLayers[index];

        const outputScale = outputCanvasElement.width / outputCanvasElement.offsetWidth;
        const outputX = layer.left * outputScale;
        const outputY = layer.top * outputScale;
        const outputWidth = layer.getScaledWidth() * outputScale;
        const outputHeight = layer.getScaledHeight() * outputScale;

        o.remoteLayers.push({
          source: layer.metadata.url,
          outputWidth: Math.min(outputWidth * scale, clipOutputWidth),
          outputHeight: Math.min(outputHeight * scale, clipOutputHeight),
          outputX: outputX * scale,
          outputY: outputY * scale,
        });
      }

      console.log(o);
      return o;
    },
    async initExport() {
      try {
        this.editStore.exporting = true;
        this.editStore.waitingForExport = true;
        await this.editStore.fastExportClip(this.exportRequestToken, await this.getOptions());
        this.snackbarStore.exportUpgradeSnackbar = false;
        try {
          // eslint-disable-next-line no-undef
          gtag("event", "ExportShort");
        } catch {
          console.log("Tracking disabled");
        }
      } catch (error) {
        this.editStore.exporting = false;
        this.snackbarStore.exportUpgradeSnackbar = false;
        this.snackbarStore.text = this.$t("exportingFailed");
        this.snackbarStore.snackbar = true;
      }
    },
    restoreLayers(config) {
      this.resetLayers();

      const outputCanvasElement = this.$refs.outputCanvas;

      for (let i = 0; i < config.layers.length; i++) {
        const layer = config.layers[i];

        const outputScale = outputCanvasElement.offsetWidth / config.outputWidth;
        const outputBoxTop = layer.outputLayerTop * outputScale;
        const outputBoxLeft = layer.outputLayerLeft * outputScale;
        const outputBoxWidth = Math.min(this.layersCanvas.width, layer.outputLayerWidth * outputScale);
        const outputBoxHeight = Math.min(this.layersCanvas.height, layer.outputLayerHeight * outputScale);

        this.addLayer(
          outputBoxTop,
          outputBoxLeft,
          outputBoxWidth,
          outputBoxHeight,
          layer.inputLayerTop,
          layer.inputLayerLeft,
          layer.inputLayerWidth,
          layer.inputLayerHeight,
          layer.enableBetween,
          false
        );
      }

      this.bringRemoteLayersToFront();
    },
    async autoCrop(forceRunningLocally = false) {
      if (this.editStore.initialized && !this.editStore.faceDetecting && !this.editStore.exporting) {
        this.editStore.faceDetecting = true;
        try {
          let layers;
          if (this.autoCropRequestToken && this.faceDetectionPresignedPost && !forceRunningLocally) {
            this.faceDetectionUrl = await this.editStore.fastDetectFaces(this.autoCropRequestToken);
            this.faceDetectionPresignedPost = null;
            layers = await this.editStore.faceDetectionsToConfig(
              await this.getOptions(true),
              this.minFaceDetectionConfidence,
              this.autoCropPadding,
              null,
              this.faceDetectionUrl
            );
          } else if (forceRunningLocally || this.faceDetectionPresignedPost) {
            this.editStore.abortControllerAutoCrop = new AbortController();
            const signal = this.editStore.abortControllerAutoCrop.signal;
            const detections = await this.editStore.detectFaces(
              this.clipSourceUrl,
              this.$refs.inputVideo.duration,
              signal
            );
            this.editStore.abortControllerAutoCrop = null;
            layers = await this.editStore.faceDetectionsToConfig(
              await this.getOptions(true),
              this.minFaceDetectionConfidence,
              this.autoCropPadding,
              deepCopy(detections)
            );
            if (this.faceDetectionPresignedPost) this.uploadFaceDetection(detections);
          } else if (this.faceDetectionUrl) {
            layers = await this.editStore.faceDetectionsToConfig(
              await this.getOptions(true),
              this.minFaceDetectionConfidence,
              this.autoCropPadding,
              null,
              this.faceDetectionUrl
            );
          }

          if (layers) this.restoreLayers(layers);
        } catch (error) {
          console.log(error);
          if (error.message === "FAST_DETECTING_FACES_FAILED") {
            this.editStore.faceDetecting = false;
            return this.autoCrop(true);
          } else if (error.message !== "DETECTING_FACES_ABORTED") {
            this.snackbarStore.text = this.$t("autoCropFailed");
            this.snackbarStore.snackbar = true;
          }
        }
        this.editStore.faceDetecting = false;
        this.isFirstAutoCrop = false;
      }
    },

    async uploadFaceDetection(detections) {
      try {
        await this.editStore.uploadFaceDetections(detections, this.faceDetectionPresignedPost);
        this.faceDetectionPresignedPost = null;
      } catch (error) {
        console.log(error);
      }
    },
    hideNotCurrentLayers() {
      const layers = this.layersCanvas.getObjects().filter((e) => e.metadata.id.startsWith("layer-"));

      layers.forEach((layer) => {
        const layerStart = layer.metadata.enableBetween[0].start;
        const layerEnd = layer.metadata.enableBetween[0].end;

        if (layerStart <= this.currentTime && layerEnd >= this.currentTime) {
          layer.set("visible", true);
        } else {
          layer.set("visible", false);
          // if (
          //   this.layersCanvas.getActiveObject().metadata.id ===
          //   layer.metadata.id
          // )
          //   this.layersCanvas.discardActiveObject();
        }
      });
    },
    reportWrongAutoCrop() {
      this.feedbackStore.tag = "other";
      this.feedbackStore.message = `${this.$t("reportWrongAutoCropFeedbackMessage", { link: window.location.href })}`;
      this.feedbackStore.feedback = true;
    },
    async fetchSubtitlesPresets() {
      try {
        if (this.accountStore.isLoggedIn && !this.subtitlesPresetStore.lastSynced)
          await this.subtitlesPresetStore.fetchSubtitlesPresets();
      } catch {
        this.snackbarStore.text = this.$t("failedToLoadCustomSubtitlesPresets");
        this.snackbarStore.snackbar = true;
      }
    },
    async fetchShortTranscript() {
      try {
        if (this.shortStore.shortTranscript._id !== this.shortStore.shortId) {
          const r = await this.shortStore.fetchShortTranscript(this.shortStore.shortId);
          const emojisUrl = r.data.short.emojisUrl;
          await this.shortStore.fetchShortEmojis(this.shortStore.shortId, emojisUrl);
        }
      } catch {
        this.snackbarStore.text = this.$t("failedToLoadShortSubtitles");
        this.snackbarStore.snackbar = true;
      }
    },
    initLayerCrop() {
      this.pauseVideo();
      const inputVideoElement = this.$refs.inputVideo;
      const tempCanvas = document.createElement("canvas");
      const tempContext = tempCanvas.getContext("2d");

      tempCanvas.width = this.clipSourceWidth;
      tempCanvas.height = this.clipSourceHeight;

      tempContext.drawImage(
        inputVideoElement,
        0,
        0,
        this.clipSourceWidth,
        this.clipSourceHeight,
        0,
        0,
        this.clipSourceWidth,
        this.clipSourceHeight
      );

      this.clipperImageUrl = tempCanvas.toDataURL("image/png");
      this.setClipperCrop();
      this.editStore.cropping = true;
    },
    debouncedInitLayerCrop: debounce(function () {
      this.initLayerCrop();
    }, 250),
    setClipperCrop() {
      this.$nextTick(() => {
        const convertCropToPercentages = (sourceCrop, fullWidth, fullHeight) => {
          return {
            top: (sourceCrop.top / fullHeight) * 100,
            left: (sourceCrop.left / fullWidth) * 100,
            width: (sourceCrop.width / fullWidth) * 100,
            height: (sourceCrop.height / fullHeight) * 100,
          };
        };

        const percentages = convertCropToPercentages(
          this.editStore.selectedLayer.metadata.sourceCrop,
          this.clipSourceWidth,
          this.clipSourceHeight
        );

        this.$refs.clipper.setTL$.next({
          top: percentages.top,
          left: percentages.left,
        });
        this.$refs.clipper.setWH$.next({
          width: percentages.width,
          height: percentages.height,
        });
      });
    },
    debouncedSetLayerCrop: debounce(function () {
      this.setLayerCrop();
    }, 250),
    setLayerCrop() {
      const clipperData = this.$refs.clipper.getDrawPos();

      this.editStore.selectedLayer.metadata.sourceCrop = {
        top: clipperData.pos.sy,
        left: clipperData.pos.sx,
        width: clipperData.pos.swidth,
        height: clipperData.pos.sheight,
      };
    },
    selectLayerId(layerId) {
      if (layerId === null) {
        this.editStore.selectedLayer = null;
        this.layersCanvas.discardActiveObject();
        this.layersCanvas.requestRenderAll();
        return;
      }

      const layer = this.layersCanvas.getObjects().find((e) => e.metadata.id === layerId);
      this.layersCanvas.setActiveObject(layer);

      this.layersCanvas.requestRenderAll();

      if (this.editStore.cropping) this.setClipperCrop();
    },
  },
  watch: {
    "editStore.aspectRatio"() {
      this.resizeCanvas(true);
    },
    "editStore.runExport"() {
      this.initExport();
    },
    "editStore.runLayerCrop"() {
      this.initLayerCrop();
    },
    "editStore.runRemoveLayer"() {
      const layerMetadata = this.editStore.selectedLayer.metadata;
      if (layerMetadata.id.startsWith("remote-layer-"))
        this.editStore.brandAssetsUrls.splice(
          this.editStore.brandAssetsUrls.findIndex((url) => url === layerMetadata.url),
          1
        );
      else this.removeLayer(layerMetadata.id);
    },
    "editStore.runAddLayer"() {
      this.addLayer();
    },
    "editStore.runAutoCrop"() {
      this.autoCrop();
    },
    "editStore.subtitlesConfig.maxCharsPerBlock"() {
      this.shortStore.editedShortTranscriptWords = this.editStore.transcriptToTranscriptWords(
        deepCopy(this.shortStore.editedShortTranscript),
        this.editStore.subtitlesConfig.maxCharsPerBlock
      );
    },
    "editStore.brandAssetsUrls"() {
      const brandAssetsUrls = this.editStore.brandAssetsUrls;
      const remoteLayers = this.layersCanvas
        .getObjects()
        .reduce(
          (acc, e) =>
            e.metadata && e.metadata.id.startsWith("remote-layer-")
              ? acc.concat({ id: e.metadata.id, url: e.metadata.url })
              : acc,
          []
        );

      console.log(brandAssetsUrls);
      remoteLayers.forEach((e) => {
        if (brandAssetsUrls.includes(e.url)) return;
        console.log("remove", e.id);
        this.removeLayer(e.id);
      });

      brandAssetsUrls.forEach((url) => {
        if (remoteLayers.findIndex((e) => e.url === url) > -1) return;
        this.addRemoteLayer(url);
      });
    },
  },
  beforeDestroy() {
    this.editStore.initialized = false;
    this.editStore.cropping = false;
    this.editStore.selectedLayer = null;
    this.editStore.editSubtitles = false;
    this.editStore.subtitlesConfigBottomSheet = false;
    this.cancelCanvasAnimation();
    window.removeEventListener("resize", debounce(this.resizeCanvas, 250));
    this.$refs.inputVideo.pause();
    this.$refs.inputVideo.src = "";
    this.$refs.inputVideo.load();
    this.$refs.inputVideo.remove();
  },
};
</script>

<style scoped>
.wrapper {
  position: relative;
  height: 525px;
}

@media (max-width: 1440px) {
  .wrapper {
    height: 425px;
  }
}

@media (max-width: 599px) {
  .editor-row {
    margin-top: 12px;
    margin-bottom: 56px;
  }

  .wrapper {
    height: 45vh;
  }
}

canvas,
.js-clipper-basic,
.v-image {
  position: absolute;
}

.ambient-shadow {
  -webkit-filter: blur(75px);
  -moz-filter: blur(75px);
  filter: blur(75px);
}

.theme--dark .ambient-shadow {
  opacity: 0.5;
}

.theme--dark .v-list.layers {
  background-color: unset;
}

.opacity-0 {
  opacity: 0;
}

:deep(.v-input--switch--inset .v-input--selection-controls__input) {
  width: 38px;
}

:deep(.v-input--selection-controls__input) {
  margin-right: 4px !important;
}

@media (min-width: 600px) {
  .controls-wrapper {
    position: relative;
    min-height: 28px;
  }

  .player-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    width: 100%;
    top: 50%;
    transform: translateY(-50%);
    width: min-content;
  }
}
</style>
